/**
 * Game Room API Service
 * Handles room creation, joining, and management
 */

import type { APIResponse } from '../types/auth';
import apiClient from './apiClient';

// Types for game room operations
export interface GameRoom {
  id?: string;
  roomId?: string; // Backend uses roomId
  name?: string;
  gameType: string;
  players: number | any[]; // Can be number or array of players
  maxPlayers: number;
  status: 'waiting' | 'playing' | 'finished';
  createdBy?: string;
  createdByUsername?: string;
  createdAt?: string;
  isPrivate?: boolean;
}

export interface CreateRoomData {
  name: string;
  gameType: string;
  maxPlayers: number;
  isPrivate: boolean;
  partnershipConfig?: string; // Partnership configuration (e.g., "2p", "3p", "4p")
  password?: string; // Required for private rooms
}

export interface UserGameStats {
  user: {
    id: string;
    name: string;
    username: string;
    email: string;
  };
  rooms: {
    active: number;
    maxAllowed: number;
    history: number;
  };
  games: {
    played: number;
    won: number;
    lost: number;
    winRate: number;
  };
  performance: {
    averageScore: number;
    bestScore: number;
    totalPoints: number;
    level: number;
    totalScore: number;
    experience: number;
    achievements: string[];
  };
  activity: {
    lastLogin: string;
    memberSince: string;
    totalSessions: number;
  };
}

class GameRoomService {

  /**
   * Get authentication headers
   */
  // Note: Authorization is injected by apiClient interceptors.

  /**
   * Get all available game rooms
   */
  async getAvailableRooms(): Promise<APIResponse<GameRoom[]>> {
    try {
      const { data } = await apiClient.get<APIResponse<GameRoom[]>>('/matchmaking/rooms');
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to fetch rooms'),
        data: [],
      };
    }
  }

  /**
   * Create a new game room
   */
  async createRoom(roomData: CreateRoomData): Promise<APIResponse<GameRoom>> {
    try {
  const endpoint = roomData.isPrivate ? '/matchmaking/rooms/private' : '/matchmaking/rooms/public';
      
      // Convert frontend format to backend format
      const backendData = {
        gameType: roomData.gameType.toLowerCase().replace(/\s+/g, '-'), // "Kadi ni Tidi" -> "kadi-ni-tidi"
        settings: {
          timePerMove: 45,
          allowSpectators: true,
          skillLevel: 'intermediate',
          maxPlayers: roomData.maxPlayers, // Pass maxPlayers in settings
          partnershipConfig: roomData.partnershipConfig // Pass partnership config
        }
      };
      
      // For private rooms, add password
      if (roomData.isPrivate && (roomData as any).password) {
        (backendData as any).password = (roomData as any).password;
      }
      
      const { data } = await apiClient.post<APIResponse<GameRoom>>(endpoint, backendData);
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to create room'),
        data: undefined,
      };
    }
  }

  /**
   * Join a game room
   */
  async joinRoom(roomId: string): Promise<APIResponse<any>> {
    try {
      const { data } = await apiClient.post<APIResponse<any>>(`/matchmaking/rooms/${roomId}/join`);
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to join room'),
        data: undefined,
      };
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<APIResponse<UserGameStats>> {
    try {
      const { data } = await apiClient.get<APIResponse<UserGameStats>>('/users/stats');
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to get user stats'),
        data: undefined,
      };
    }
  }

  /**
   * Add AI players to a room
   */
  async addAIPlayers(roomId: string, count?: number): Promise<APIResponse<{
    addedCount: number;
    totalPlayers: number;
    maxPlayers: number;
  }>> {
    try {
      const { data } = await apiClient.post<APIResponse<{ addedCount: number; totalPlayers: number; maxPlayers: number }>>(
        `/matchmaking/rooms/${roomId}/add-ai`,
        { count }
      );
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to add AI players'),
        data: undefined,
      };
    }
  }

  /**
   * Set player ready status in a room
   */
  async setPlayerReady(roomId: string, ready: boolean): Promise<APIResponse<{
    playerId: string;
    isReady: boolean;
    allPlayersReady?: boolean;
    gameStarted?: boolean;
  }>> {
    try {
      const { data } = await apiClient.post<APIResponse<{ playerId: string; isReady: boolean; allPlayersReady?: boolean; gameStarted?: boolean }>>(
        `/matchmaking/rooms/${roomId}/ready`,
        { ready }
      );
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to set ready status'),
        data: undefined,
      };
    }
  }

  /**
   * Get user's active rooms
   */
  async getUserActiveRooms(): Promise<APIResponse<{
    activeRooms: any[];
    totalActiveRooms: number;
    maxActiveRooms: number;
    canJoinNewRoom: boolean;
  }>> {
    try {
      const { data } = await apiClient.get<APIResponse<{ activeRooms: any[]; totalActiveRooms: number; maxActiveRooms: number; canJoinNewRoom: boolean }>>(
        '/user/rooms'
      );
      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || (error instanceof Error ? error.message : 'Failed to get active rooms'),
        data: undefined,
      };
    }
  }
}

// Export singleton instance
export const gameRoomService = new GameRoomService();
export default gameRoomService;